/**
 * Superadmin Authentication Utilities
 * 
 * Centralized helpers for superadmin logout and redirect flows.
 * All superadmin logout/redirect operations should go through these helpers
 * to ensure they pass through the forceLogout fuse.
 */

import { createForceLogout, ForceLogoutOptions } from './forceLogout';
import { useQueryClient } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';

/**
 * Logout superadmin and redirect to login page
 * 
 * This helper ensures all superadmin logout flows go through forceLogout
 * with proper reason tracking, so the fuse can block invalid logouts.
 * 
 * @param debugReason - Human-readable reason for debugging (e.g., 'Header::handleLogout')
 * @param options - Additional options for forceLogout
 */
export function logoutSuperadminToLogin(
  debugReason: string,
  options?: Partial<ForceLogoutOptions>
) {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : undefined;
  
  console.groupCollapsed('[SuperadminLogout] Redirecting to /superadmin/login');
  console.log({
    debugReason,
    pathname,
    from: 'logoutSuperadminToLogin',
    options,
  });
  console.groupEnd();
  
  try {
    // Use the global queryClient instance
    const forceLogout = createForceLogout(queryClient);
    // CRITICAL: Normalize manual logout reasons to 'manual_click' which is now accepted by forceLogout
    // For auto logout attempts, use the provided reason (which will be blocked by forceLogout guard)
    const finalReason = options?.reason === 'manual' || 
                        options?.reason === 'manual_click' || 
                        options?.reason === 'user_logout'
      ? 'manual_click' // Normalize manual reasons to manual_click (now accepted by forceLogout for superadmin)
      : (options?.reason || 'superadmin_auth_failed'); // Keep auto reasons as-is (will be blocked)
    forceLogout({
      reason: finalReason,
      source: debugReason,
      ...options,
    });
  } catch (err) {
    console.error('[SuperadminLogout] Error in logoutSuperadminToLogin:', err);
    // Fallback: direct redirect (but still log it)
    if (typeof window !== 'undefined') {
      const reasonParam = options?.reason ? `?logoutReason=${encodeURIComponent(options.reason)}` : '';
      window.location.href = `/superadmin/login${reasonParam}`;
    }
  }
}

/**
 * Hook version that uses React Query client from context
 * Use this inside React components/hooks when you have access to QueryClientProvider
 */
export function useLogoutSuperadmin() {
  const queryClient = useQueryClient();
  
  return (debugReason: string, options?: Partial<ForceLogoutOptions>) => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : undefined;
    
    console.groupCollapsed('[SuperadminLogout] Redirecting to /superadmin/login');
    console.log({
      debugReason,
      pathname,
      from: 'useLogoutSuperadmin',
      options,
    });
    console.groupEnd();
    
    const forceLogout = createForceLogout(queryClient);
    forceLogout({
      reason: options?.reason || 'superadmin_auth_failed',
      source: debugReason,
      ...options,
    });
  };
}

